<template>
  <div
    class="home-banner-carousel"
    @pointerenter="pauseForMouse"
    @pointerleave="resumeForMouse"
    @focusin="pauseForKeyboard"
    @focusout="resumeAutoplay"
  >
    <swiper
      :loop="hasManySlides"
      :autoplay="autoplay"
      :pagination="pagination"
      :a11y="A11Y_PT_BR"
      :preloadImages="false"
      @swiper="onSwiper"
      @slideChangeTransitionEnd="syncSlidesForAssistiveTech"
    >
      <swiper-slide v-for="(slide, index) in slides" :key="index">
        <a
          :href="slide.href"
          target="_blank"
          rel="noopener"
          :aria-label="`${slide.alt} (abre em nova aba)`"
        >
          <figure class="image is-2by1 banner">
            <img
              :src="slide.image_url"
              :alt="slide.alt"
              :loading="index === 0 ? 'eager' : 'lazy'"
            />
          </figure>
        </a>
      </swiper-slide>
    </swiper>
  </div>
</template>

<script>
import { A11Y_PT_BR } from "utils/swiper";
import { Swiper, SwiperSlide } from "swiper/vue";

import { computed, ref } from "vue";

export default {
  components: {
    SwiperSlide,
    swiper: Swiper,
  },
  props: {
    slides: { type: Array, default: () => [] },
  },
  setup(props) {
    const swiperEl = ref(null);

    const hasManySlides = computed(() => props.slides.length > 1);

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    const autoplay = computed(() =>
      hasManySlides.value && !prefersReducedMotion
        ? { delay: 5000, disableOnInteraction: false }
        : undefined,
    );

    const pagination = computed(() =>
      hasManySlides.value ? { clickable: true } : undefined,
    );

    const pauseAutoplay = () => {
      if (!hasManySlides.value) return;

      swiperEl.value?.autoplay?.stop();
    };

    const resumeAutoplay = () => {
      if (!hasManySlides.value) return;

      swiperEl.value?.autoplay?.start();
    };

    const pauseForMouse = (event) =>
      event.pointerType === "mouse" && pauseAutoplay();

    const resumeForMouse = (event) =>
      event.pointerType === "mouse" && resumeAutoplay();

    const pauseForKeyboard = (event) =>
      event.target.matches(":focus-visible") && pauseAutoplay();

    const syncSlidesForAssistiveTech = (swiper) => {
      Array.from(swiper.slides).forEach((slide, index) => {
        const visible = index === swiper.activeIndex;
        const position = Number(slide.dataset.swiperSlideIndex ?? index) + 1;

        slide.setAttribute("aria-hidden", String(!visible));
        slide.setAttribute(
          "aria-label",
          `${position} de ${props.slides.length}`,
        );
        slide.querySelectorAll("a").forEach((link) => {
          link.tabIndex = visible ? 0 : -1;
        });
      });
    };

    const onSwiper = (instance) => {
      swiperEl.value = instance;
      syncSlidesForAssistiveTech(instance);
    };

    return {
      A11Y_PT_BR,
      hasManySlides,
      autoplay,
      pagination,
      onSwiper,
      syncSlidesForAssistiveTech,
      pauseForMouse,
      resumeForMouse,
      pauseForKeyboard,
      resumeAutoplay,
    };
  },
};
</script>
